import { useCallback, useEffect, useRef, useState } from "react"
import { useFetcher } from "react-router"

import { useAutoDismiss } from "./useAutoDismiss"

interface ConfirmationTokenLike {
  token: string
  expires: string
}

interface ConfirmableActionResult<TConfirmation extends ConfirmationTokenLike = ConfirmationTokenLike> {
  errorMessages?: string[]
  successMessage?: string
  confirmationData?: TConfirmation
}

export function useConfirmationCountdown(expires: string | undefined): number | undefined {
  const [timeLeft, setTimeLeft] = useState<number>()

  useEffect(() => {
    if (expires == null) {
      setTimeLeft(undefined)
      return
    }

    const expiresAt = new Date(expires).getTime()
    const getTimeLeft = () => Math.max(0, expiresAt - Date.now()) / 1000

    const initial = getTimeLeft()
    if (initial <= 0) {
      setTimeLeft(undefined)
      return
    }

    setTimeLeft(initial)

    const intervalId = setInterval(() => {
      const remaining = getTimeLeft()
      setTimeLeft(remaining > 0 ? remaining : undefined)
      if (remaining <= 0) clearInterval(intervalId)
    }, 1_000)

    return () => clearInterval(intervalId)
  }, [expires])

  return timeLeft
}

export function useConfirmableFetcher<TResult extends ConfirmableActionResult>() {
  const fetcher = useFetcher<TResult>()
  const formRef = useRef<HTMLFormElement>(null)
  const reset = fetcher.reset

  const confirmationData = fetcher.data?.confirmationData as TResult["confirmationData"] | undefined
  const errorMessages = fetcher.data?.errorMessages as TResult["errorMessages"] | undefined
  const successMessage = fetcher.data?.successMessage as TResult["successMessage"] | undefined

  const confirmationTimeLeft = useConfirmationCountdown(confirmationData?.expires)
  const hasPendingToken = confirmationData != null && confirmationTimeLeft != null
  const timeLeft = hasPendingToken ? (confirmationTimeLeft ?? 0) : 0

  const isSubmitting = fetcher.state !== "idle"

  useAutoDismiss(successMessage != null, reset)

  const confirmAndSubmit = useCallback(() => {
    if (!confirmationData || timeLeft === 0) return
    formRef.current?.requestSubmit()
  }, [confirmationData, timeLeft])

  return {
    fetcher,
    formRef,
    confirmationData,
    errorMessages,
    successMessage,
    hasPendingToken,
    timeLeft,
    isSubmitting,
    reset,
    confirmAndSubmit,
  }
}
